/**
 * Pure helpers for the "Export operation" feature.
 *
 * Mirrors war-plan-export but with a much simpler scope:
 *   - All operation-mode objects are exportable (no kind discrimination).
 *   - All operation-mode arrows are exportable.
 *   - No facility / msup fusion logic.
 *
 * Callers must pass objects/arrows already filtered to `mode === 'operation'`.
 */

import type { MapArrow, MapObject } from '@warplan/shared';
import { findRegionKey } from './foxhole-maps.js';

const EMPTY_IDS: ReadonlySet<string> = new Set();

/** Filter operation markers for the final export image. */
export function filterOperationMarkersForExport(
  objects: ReadonlyArray<MapObject>,
  disabled: ReadonlySet<string>,
  excludedIds: ReadonlySet<string> = EMPTY_IDS,
): MapObject[] {
  return objects.filter((o) => {
    if (excludedIds.has(o.id)) return false;
    if (disabled.size === 0) return true;
    const key = findRegionKey(o.position.x, o.position.y);
    return key === null || !disabled.has(key);
  });
}

/** Filter operation arrows for the final export image (by origin vertex). */
export function filterOperationArrowsForExport(
  arrows: ReadonlyArray<MapArrow>,
  disabled: ReadonlySet<string>,
  excludedIds: ReadonlySet<string> = EMPTY_IDS,
): MapArrow[] {
  return arrows.filter((a) => {
    if (excludedIds.has(a.id)) return false;
    const origin = a.points[0];
    if (!origin) return false;
    if (disabled.size === 0) return true;
    const key = findRegionKey(origin.x, origin.y);
    return key === null || !disabled.has(key);
  });
}

export function buildOperationExportFileName(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `operation-${y}${m}${d}-${hh}${mm}.png`;
}
